import { useAuth } from '@agentnet/auth';
import { GetFileInfoProps } from 'api/file/file-api';
import { updateFincenTransfereeAPI } from 'api/fincen-report-api';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import useAsync from 'hooks/useAsync';
import { useAutoSaveNavigation, useFincenAutoSave } from 'hooks/useAutoSave';
import { useNavigationLoading } from 'hooks/useNavigationLoading';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AsyncStatus, MAX_ITEMS_LIMIT } from '../constants';
import { FincenUpdateResponse, Transferee, TransfereeParty } from '../types';
import { TransfereeType } from './constants';
import { getInitialTransfereeData } from './models/transferee';
import TransfereeInformationContent from './TransfereeInformationContent';

/**
 * Transform party data before sending to API
 * Remove fields that are only applicable to trusteeLegalEntity party type
 */
const transformPartyDataForAPI = (party: TransfereeParty): TransfereeParty => {
  const transformedParty = { ...party };

  // If party type is not trusteeLegalEntity, remove trusteeLegalEntity-specific fields and restructure individual name fields
  if (party.partyType !== 'trusteeLegalEntity') {
    delete transformedParty.foreignBusinessNoUSLocation;
    delete transformedParty.addressType;
    delete transformedParty.foreignPrincipalAddress;

    // Restructure individual name fields under 'individual' object
    const individual: any = {};
    if (transformedParty.firstName !== undefined) {
      individual.firstName = transformedParty.firstName;
      delete transformedParty.firstName;
    }
    if (transformedParty.middleName !== undefined) {
      individual.middleName = transformedParty.middleName;
      delete transformedParty.middleName;
    }
    if (transformedParty.lastName !== undefined) {
      individual.lastName = transformedParty.lastName;
      delete transformedParty.lastName;
    }
    if (transformedParty.suffix !== undefined) {
      individual.suffix = transformedParty.suffix;
      delete transformedParty.suffix;
    }

    // Only add individual object if it has properties
    if (Object.keys(individual).length > 0) {
      (transformedParty as any).individual = individual;
    }
  }

  return transformedParty;
};

/**
 * Transform transferee data before sending to API
 */
const transformTransfereeDataForAPI = (transferees: Transferee[]): Transferee[] => {
  return transferees.map((transferee) => {
    const transformed: Transferee = {
      ...transferee,
      // Transform each party in the parties array
      parties: transferee.parties?.map((party) => transformPartyDataForAPI(party)),
    };

    // Convert transfereeDbaName from comma-separated string to array
    if (transferee.transfereeDbaName && typeof transferee.transfereeDbaName === 'string') {
      const dbaNames = transferee.transfereeDbaName;
      // API expects array format, override the property
      Object.assign(transformed, {
        transfereeDbaName: dbaNames && dbaNames.trim() ? [dbaNames.trim()] : undefined,
      });
    }

    return transformed;
  });
};

/**
 * Transform transferee data from API for UI display
 */
const transformTransfereeDataFromAPI = (transferees: Transferee[]): Transferee[] => {
  return transferees.map((transferee) => {
    const transformed: Transferee = { ...transferee };

    // Convert transfereeDbaName from array to comma-separated string for UI
    if (Array.isArray(transferee.transfereeDbaName)) {
      transformed.transfereeDbaName = transferee.transfereeDbaName.join(', ');
    }

    return transformed;
  });
};

const TransfereeInformationContainer: FC = () => {
  const { addSnackbarMessage } = useSnackBars();
  const { fileId } = useParams<{ fileId: string }>();
  const history = useHistory();
  const fileDataContext = useContext(FileDataContext) as FileDataContextInterface;
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const {
    fincenReport,
    fincenReportStatus,
    updateTransferee,
    transfereeValidationErrors,
    clearTransfereeValidationErrors,
  } = fincenReportCtx ?? {};

  // Load file data when lost on refresh
  useEffect(() => {
    if (fileId && fileDataContext && !fileDataContext.fileData?.fileId && fileDataContext.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataContext.loadFileData(fileProps);
    }
  }, [fileId, fileDataContext]);

  const [transferees, setTransferees] = useState<Transferee[]>([
    {
      ...getInitialTransfereeData(),
      id: `transferee_${Date.now()}`,
    },
  ]);
  const { getAccessToken } = useAuth();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [shouldNavigateOnComplete, setShouldNavigateOnComplete] = useState(false);
  const isNavigating = useNavigationLoading();

  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!fincenReport) return;
    if (fincenReport.reportStatusCdId === 9302) return; // Disable auto-save once report generated
    const token = await getAccessToken();
    const transformedTransferees = transformTransfereeDataForAPI(transferees);
    await updateFincenTransfereeAPI(String(fincenReport.fincenReportId || 0), transformedTransferees, token);
  }, [fincenReport, getAccessToken, transferees]);

  // Use generic auto-save navigation hook
  useAutoSaveNavigation('transferee-information', handleAutoSave, fileId, fincenReport?.reportStatusCdId !== 9302);

  const { triggerSave, isSaving } = useFincenAutoSave({
    data: transferees,
    updateFunction: updateTransferee,
    apiSaveFunction: updateFincenTransfereeAPI,
    reportId: fincenReport?.fincenReportId,
    hasContent: (data) => data.some((t) => !!(t.transfereeLegalName || t.transfereeDbaName)),
    transformDataForAPI: transformTransfereeDataForAPI,
    enabled: fincenReport?.reportStatusCdId !== 9302,
    errorContext: 'transferee information',
  });

  useEffect(() => {
    if (fincenReportCtx?.fincenReport?.transferee) {
      if (fincenReportCtx.fincenReport?.transferee.length > 0) {
        // Transform data from API format to UI format
        const transformedTransferees = transformTransfereeDataFromAPI(fincenReportCtx.fincenReport.transferee);
        setTransferees(transformedTransferees);
      }
    }
  }, [fincenReportCtx?.fincenReport?.transferee]);

  useEffect(() => {
    if (formSubmitted) {
      executeUpdateTransfereeData();
    }
  }, [formSubmitted]);

  const updateTransfereeData = async (): Promise<FincenUpdateResponse> => {
    const token = await getAccessToken();
    // Transform the transferee data before sending to API
    const transformedTransferees = transformTransfereeDataForAPI(transferees);
    return updateFincenTransfereeAPI(String(fincenReport?.fincenReportId || 0), transformedTransferees, token);
  };

  const {
    execute: executeUpdateTransfereeData,
    value: updateTransfereeResponse,
    status: updateTransfereeStatus,
    errors: updateTransfereeErrors,
  } = useAsync<FincenUpdateResponse>(updateTransfereeData, false);

  useEffect(() => {
    if (updateTransfereeStatus === AsyncStatus.SUCCESS) {
      if (shouldNavigateOnComplete) {
        setShouldNavigateOnComplete(false);
        history.push(`/files/${fileId}/fincen-reporting/transferor-info`);
      } else {
        addSnackbarMessage({
          message: 'Saving Successful',
          type: 'success',
        });
      }
      fincenReportCtx?.reloadFincenReport?.(fileId ?? '0');
      setFormSubmitted(false);
    }
  }, [updateTransfereeStatus, addSnackbarMessage, shouldNavigateOnComplete]);

  const handleSubmissionComplete = (forceNavigate?: boolean) => {
    setFormSubmitted(false);
    if (forceNavigate || shouldNavigateOnComplete) {
      setShouldNavigateOnComplete(false);
      history.push(`/files/${fileId}/fincen-reporting/transferor-info`);
    }
  };

  const handleAddTransferee = () => {
    if (transferees.length >= MAX_ITEMS_LIMIT) {
      return;
    }
    const newTransferee = {
      ...getInitialTransfereeData(),
      id: `transferee_${Date.now()}`,
    };
    setTransferees([...transferees, newTransferee]);
  };

  const handleUpdateTransferee = (transferee: Transferee, index: number) => {
    const updatedTransferees = [...transferees];
    updatedTransferees[index] = {
      ...transferee,
      transfereeType: transferee.transfereeType as TransfereeType,
    };
    setTransferees(updatedTransferees);
  };

  const handleDeleteTransferee = (index: number) => {
    const updatedTransferees = [...transferees];
    updatedTransferees.splice(index, 1);
    setTransferees(updatedTransferees);
  };

  const handleSaveReport = async () => {
    if (updateTransferee) {
      updateTransferee(transferees);
    }
    setFormSubmitted(true);
  };

  const handleNext = () => {
    if (fincenReport?.reportStatusCdId === 9302) {
      history.push(`/files/${fileId}/fincen-reporting/transferor-info`);
      return;
    }
    if (updateTransferee) {
      updateTransferee(transferees);
    }
    setShouldNavigateOnComplete(true);
    setFormSubmitted(true);
  };

  const handleBack = () => {
    history.push(`/files/${fileId}/fincen-reporting/property-info`);
  };

  // Redirect to files if fileId is not present (handles browser refresh edge case)
  if (!fileId) {
    return <Redirect to="/files" />;
  }

  return (
    <>
      <LoadingSpinner
        status={formSubmitted || isNavigating || fincenReportStatus === 'pending' ? 'pending' : 'success'}
        variant="linear"
      />
      <TransfereeInformationContent
        transferees={transferees}
        loadingStatus={'idle'}
        onAddTransferee={handleAddTransferee}
        onUpdateTransferee={handleUpdateTransferee}
        onDeleteTransferee={handleDeleteTransferee}
        validationErrors={transfereeValidationErrors}
        clearValidationErrors={clearTransfereeValidationErrors}
        isReadOnly={fincenReport?.reportStatusCdId === 9302}
      />
      <BottomAppBar
        cancelLabel="Back"
        onCancel={handleBack}
        cancelDataQa="fincen-transferee-back-button"
        secondaryActionLabel="Save Report"
        onSecondaryClick={handleSaveReport}
        secondaryActionDataQa="fincen-transferee-save-button"
        primaryActionLabel="Next"
        onPrimaryClick={handleNext}
        primaryActionDataQa="fincen-transferee-next-button"
        disableSecondary={fincenReport?.reportStatusCdId === 9302}
      />
    </>
  );
};

export default TransfereeInformationContainer;
